<script setup lang="ts">

const WrThankYou = defineAsyncComponent(
  () => import("~/components/molecules/WrThankYou.vue"),
);
import WrForm from './WrForm.vue';

interface HeroProduct {
  name: string;
  blurb: string;
  icon: string;
  link?: string;
}

withDefaults(
  defineProps<{
    headingTop?: string;
    headingBottom?: string;
    subcopy?: string;
    chips?: string[];
    productsLabel?: string;
    products?: HeroProduct[];
    formPage?: string;
  }>(),
  {
    headingTop: "Transforming Ideas",
    headingBottom: "with Next-Gen AI",
    subcopy:
      "Ready to scale with enterprise-grade Artificial Intelligence or launch an ambitious project? Let’s innovate together. Reach out or explore our proprietary AI suites below.",
    chips: () => ["800+ projects delivered", "Since 2010", "1M+ hours of development"],
    productsLabel: "Powered by Vaah.ai Ecosystem",
    products: () => [
      {
        name: "LLM KB",
        blurb:
          "The brain of your AI stack — a knowledge base built on vectors, WIKIs and knowledge graphs.",
        icon: "WrFolder",
        link: "https://llmkb.ai/",
      },
      {
        name: "VaahAgents",
        blurb:
          "AI agents that execute sequential prompts end-to-end to research, act and report on complex tasks.",
        icon: "WrAiIcon",
      },
      {
        name: "GenicUI",
        blurb: "Generative UI platform that turns prompts into production-ready interfaces.",
        icon: "WrCmsIcon",
        link: "https://genicui.vaah.ai/",
      },
    ],
    formPage: "",
  },
);

const isSuccess = ref(false);
</script>

<template>
  <section class="flex max-lg:flex-wrap justify-between gap-8 relative z-10">
    <span
      class="hidden dark:block size-[1200px] bg-[linear-gradient(136.28deg,_rgba(51,_4,_79,_0)_30.43%,_#260049_54.53%,_#36006F_92.37%)] blur-[150px] rounded-full shadow-[49px_11px_53.1px_15px_#230236] absolute -top-80 -left-64 opacity-80" />
    <span
      class="dark:hidden h-[865px] w-[1068px] bg-[rgba(73,2,166,0.1)] blur-[150px] rounded-full absolute top-[90px] -right-[500px] opacity-90" />

    <span
      class="dark:hidden h-[679px] w-[403px] bg-[#ffffff] blur-[150px] rounded-full absolute -top-[81px] -left-[225px] opacity-100" />

    <span
      class="hidden dark:block size-[297px] bg-[linear-gradient(157.91deg,_rgba(51,_4,_79,_0)_9.32%,_#36006F_86.78%)] rounded-full blur-[150px] absolute top-96 -right-72" />

    <div class="xl:max-w-[567px] lg:max-w-[467px] w-full z-10 max-lg:contents">
      <div class="w-full z-10 max-lg:order-1 lg:pr-12 pr-3 max-md:text-center">
        <h1 class="mb-6 md:mb-8">
          <WrGradientText :text="headingTop" append-class="pb-0 lg:pb-2" />
          {{ headingBottom }}
        </h1>
        <p class="font-normal text-lg lg:text-xl tracking-[0.5px]">
          {{ subcopy }}
        </p>
        <div class="flex flex-wrap gap-2 lg:mt-3 mt-6 max-md:justify-center">
          <span v-for="chip in chips" :key="chip"
            class="rounded-full px-2 py-2 text-sm tracking-[0.5px] border border-[rgba(61,27,96,0.2)] bg-white/60 dark:border-[#3D1B60] dark:bg-[rgba(1,3,20,0.4)]">
            {{ chip }}
          </span>
        </div>
      </div>
      <div class="w-full z-10 max-lg:order-3 lg:pr-12 pr-3">
        <div class="lg:mt-5">
          <p class="font-avant text-sm uppercase tracking-[2px] opacity-60 mb-4 max-md:text-center">
            {{ productsLabel }}
          </p>
          <ul class="flex flex-col gap-4">
            <li v-for="product in products" :key="product.name" class="flex items-start gap-3">
              <WrSVGIcon :name="product.icon" class="size-9 shrink-0 text-[var(--primary)]" />
              <div class="max-w-none">
                <p class="font-semibold text-base leading-tight">
                  <NuxtLink v-if="product.link" :to="product.link" target="_blank" rel="noopener noreferrer"
                    class="transition-colors hover:underline">
                    {{ product.name }}
                  </NuxtLink>
                  <span v-else>{{ product.name }}</span>
                </p>
                <p class="text-sm tracking-[0.5px] opacity-70 mt-0.5">
                  {{ product.blurb }}
                </p>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
    <div class="z-10 max-lg:order-2 max-lg:w-full">
        <WrThankYou v-if="isSuccess" @back-to-form="isSuccess = false" />
        <WrForm v-else :page="formPage" @formsubmitted="isSuccess = true" />
    </div>
  </section>
</template>
